import { Box, Skeleton } from '@mui/material'

import { ArtistPostVideoFragment, Maybe } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenuProps } from '@/components/layouts'
import { NotFound } from '@/components/pages/Errors/NotFound'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter } from '@/core'
import { Cell, Template } from '@/core/architecture'

import { ShortVideoModal } from '../../ArtistPostVideos/template/ShortVideoModal'

export * from './fragments'

export type State = {
  posts: ArtistPostVideoFragment[]
  isInvalidPost: boolean
  postIndex: number
  initialLoading: boolean
  refetching: boolean
  hasNextPage: boolean
  user?: Maybe<SideMenuProps['user']>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onLikePost: (
    postId: string,
    isLike: boolean,
    likeCount: number
  ) => Promise<void>
  onReportArtist?: (
    postId: string,
    artistId: string,
    accountId: string
  ) => Promise<void>
  onBlockUser: (id: string) => Promise<void | boolean>
  onClose: () => void
}

export const PostVideoDetailModalTemplate: Template<State, Action> = ({
  state,
  ...actions
}) => {
  const { routerNames } = useRouter()
  return (
    <ArtistThemeProvider>
      <Head title={routerNames.PostVideoDetailModal} />
      <BaseLayout margin>
        <PostVideoDetailModalCell state={state} {...actions} />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

export const PostVideoDetailModalCell: Cell<{ state: State } & Action> = ({
  state,
  onFetchMore,
  onLikePost,
  onReportArtist,
  onBlockUser,
  onClose,
}) => {
  if (state.initialLoading)
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Skeleton variant="rounded" height="90%" width="90%" />
      </Box>
    )
  if (state.isInvalidPost) {
    return <NotFound useNextRouter={false} />
  }

  return (
    <ShortVideoModal
      open={true}
      onClose={onClose}
      onFetchMore={onFetchMore}
      onLike={onLikePost}
      onReportArtist={onReportArtist}
      onBlockUser={onBlockUser}
      isMembershipPersonalInformationRequired={
        !!state.user?.isMembershipPersonalInformationRequired
      }
      {...state}
    />
  )
}
